import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, ArrowLeft, Clock, Play } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { bigCelebrate } from '../lib/celebrate';

const ExamPage = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState([]);

  useEffect(() => {
    fetchExamData();
  }, [lessonId]);

  const fetchExamData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');

      // Fetch lesson
      const lessonRes = await fetch(`${backendUrl}/api/video-lessons/${lessonId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (lessonRes.ok) {
        const lessonData = await lessonRes.json();
        setLesson(lessonData);
      }

      // Fetch exam
      const examRes = await fetch(`${backendUrl}/api/exams/lesson/${lessonId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (examRes.ok) {
        const examData = await examRes.json();
        setExam(examData.exam);
      }

      // Fetch previous attempts
      const attemptsRes = await fetch(`${backendUrl}/api/exam-attempts/lesson/${lessonId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (attemptsRes.ok) {
        const attemptsData = await attemptsRes.json();
        setAttempts(attemptsData.attempts || []);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching exam:', error);
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({
      ...answers,
      [questionId]: answer
    });
  };

  const handleSubmit = async () => {
    if (!exam) return;

    // Check all questions answered
    const unanswered = exam.questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      alert(`Please answer all questions. ${unanswered.length} question(s) remaining.`);
      return;
    }

    setSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');

      const userAnswers = exam.questions.map(q => ({
        question_id: q.id,
        answer: answers[q.id]
      }));

      const response = await fetch(`${backendUrl}/api/exam-attempts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          exam_id: exam.id,
          answers: userAnswers
        })
      });

      if (response.ok) {
        const resultData = await response.json();
        setResult(resultData);
        if (resultData?.passed) {
          // Celebrate with a 2-cannon confetti burst on exam pass
          setTimeout(() => bigCelebrate(), 200);
        }
      } else {
        alert('Failed to submit exam. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting exam:', error);
      alert('Error submitting exam. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setResult(null);
    fetchExamData();
  };

  const handleBackToCourse = () => {
    if (lesson) {
      navigate(`/course/${lesson.course_id}`);
    } else {
      navigate('/courses');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading exam...</p>
        </div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <p className="text-gray-600 mb-4">No exam available for this lesson.</p>
            <button
              onClick={handleBackToCourse}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Back to Course
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show results
  if (result) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <div className="text-center mb-8">
              {result.passed ? (
                <>
                  <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Congratulations!
                  </h1>
                  <p className="text-lg text-gray-600 mb-4">
                    You passed the exam!
                  </p>
                </>
              ) : (
                <>
                  <XCircle className="w-20 h-20 text-orange-600 mx-auto mb-4" />
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Keep Trying!
                  </h1>
                  <p className="text-lg text-gray-600 mb-4">
                    You didn't pass this time, but you can retake the exam.
                  </p>
                </>
              )}
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <p className="text-4xl font-bold text-purple-600 mb-2">
                  {Math.round(result.score)}%
                </p>
                <p className="text-gray-600">
                  Passing score: {exam.passing_score}%
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Attempt #{result.attempt_number}
                </p>
              </div>

              {result.next_lesson_unlocked && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                  <p className="text-purple-800 font-medium">
                    ✨ Next lesson unlocked!
                  </p>
                </div>
              )}
            </div>

            {/* Review of incorrect questions */}
            {Array.isArray(result.incorrect_questions) && result.incorrect_questions.length > 0 && (
              <div
                data-testid="exam-missed-questions"
                className="mb-8 border border-orange-200 bg-orange-50 rounded-xl p-6"
              >
                <h2 className="text-lg font-bold text-orange-900 mb-1">
                  Review the {result.incorrect_questions.length} question{result.incorrect_questions.length === 1 ? '' : 's'} you missed
                </h2>
                <p className="text-sm text-orange-800/80 mb-5">
                  Go back to the lesson, then retake the exam when you're ready.
                </p>

                <div className="space-y-5">
                  {result.incorrect_questions.map((q, idx) => {
                    const findOptionText = (label) => {
                      if (!Array.isArray(q.options)) return null;
                      const opt = q.options.find((o) => o.label === label);
                      return opt ? `${opt.label}. ${opt.text}` : label;
                    };
                    const yourDisplay =
                      q.question_type === 'multiple_choice' && q.your_answer
                        ? findOptionText(q.your_answer)
                        : (q.your_answer || '—');
                    const correctDisplay =
                      q.question_type === 'multiple_choice' && q.correct_answer
                        ? findOptionText(q.correct_answer)
                        : (q.correct_answer || '—');

                    const ts = q.lesson_timestamp_seconds;
                    const hasTimestamp = typeof ts === 'number' && ts >= 0;
                    const formatTs = (secs) => {
                      const total = Math.max(0, Math.floor(secs));
                      const h = Math.floor(total / 3600);
                      const m = Math.floor((total % 3600) / 60);
                      const s = total % 60;
                      const pad = (n) => String(n).padStart(2, '0');
                      return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
                    };
                    const handleReviewClick = () => {
                      if (!q.course_id || !q.lesson_id) return;
                      navigate(`/course/${q.course_id}?lesson=${q.lesson_id}&t=${ts || 0}`);
                    };

                    return (
                      <div
                        key={q.question_id || idx}
                        data-testid={`missed-question-${idx}`}
                        className="bg-white rounded-lg border border-orange-200 p-4"
                      >
                        <p className="text-sm font-semibold text-orange-700 mb-1">
                          Question {idx + 1}
                        </p>
                        <p className="text-gray-900 font-medium mb-3">
                          {q.question_text}
                        </p>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2 text-red-700">
                            <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>
                              <span className="font-semibold">Your answer:</span>{' '}
                              <span data-testid={`missed-your-answer-${idx}`}>{yourDisplay}</span>
                            </span>
                          </div>
                          <div className="flex items-start gap-2 text-green-700">
                            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>
                              <span className="font-semibold">Correct answer:</span>{' '}
                              <span data-testid={`missed-correct-answer-${idx}`}>{correctDisplay}</span>
                            </span>
                          </div>
                        </div>
                        {hasTimestamp && q.course_id && q.lesson_id && (
                          <button
                            type="button"
                            onClick={handleReviewClick}
                            data-testid={`review-at-timestamp-${idx}`}
                            className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-800 text-sm font-medium transition-colors"
                          >
                            <Play className="w-4 h-4" />
                            Review this part at {formatTs(ts)} in the lesson
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleBackToCourse}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                {result.passed ? 'Continue to Next Lesson' : 'Back to Course'}
              </button>
              
              {!result.passed && result.can_retake && (
                <button
                  onClick={handleRetake}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Retake Exam
                </button>
              )}
            </div>

            {/* Previous Attempts */}
            {attempts.length > 0 && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Previous Attempts</h3>
                <div className="space-y-2">
                  {attempts.map((attempt, index) => (
                    <div key={attempt.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Attempt #{attempt.attempt_number}</span>
                      <span className={`text-sm font-semibold ${
                        attempt.passed ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {Math.round(attempt.score)}% {attempt.passed ? '✓' : '✗'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show exam questions
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={handleBackToCourse}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Course
        </button>

        <div className="bg-white rounded-2xl shadow-sm p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {exam.title}
            </h1>
            {exam.description && (
              <p className="text-gray-600 mb-4">{exam.description}</p>
            )}
            
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Passing Score</p>
                <p className="text-lg font-bold text-purple-600">{exam.passing_score || 70}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Questions</p>
                <p className="text-lg font-bold text-gray-900">{(exam.questions || []).length}</p>
              </div>
              {attempts.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600">Your Attempts</p>
                  <p className="text-lg font-bold text-gray-900">{attempts.length}</p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-8">
            {(!exam.questions || exam.questions.length === 0) ? (
              <div className="text-center py-8">
                <XCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">No Questions Available</h2>
                <p className="text-gray-600 mb-6">
                  This exam doesn't have any questions yet. Please contact your instructor.
                </p>
                <button
                  onClick={handleBackToCourse}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold"
                >
                  Back to Course
                </button>
              </div>
            ) : (
            exam.questions.map((question, index) => (
              <div key={question.id || index} className="border-b border-gray-200 pb-8 last:border-0">
                <div className="mb-4">
                  <span className="text-sm font-semibold text-purple-600 mb-2 block">
                    Question {index + 1} of {exam.questions.length}
                  </span>
                  <p className="text-lg font-medium text-gray-900">
                    {question.question_text}
                  </p>
                </div>

                {question.question_type === 'multiple_choice' && question.options && (
                  <div className="space-y-3">
                    {question.options.map((option, optIdx) => (
                      <label
                        key={option.label || optIdx}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          answers[question.id] === option.label
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={option.label}
                          checked={answers[question.id] === option.label}
                          onChange={() => handleAnswerChange(question.id, option.label)}
                          className="w-5 h-5 text-purple-600"
                        />
                        <span className="ml-3 text-gray-900">{option.text}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.question_type === 'true_false' && (
                  <div className="space-y-3">
                    {['True', 'False'].map((option) => (
                      <label
                        key={option}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          answers[question.id] === option
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={option}
                          checked={answers[question.id] === option}
                          onChange={() => handleAnswerChange(question.id, option)}
                          className="w-5 h-5 text-purple-600"
                        />
                        <span className="ml-3 text-gray-900">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.question_type === 'short_answer' && (
                  <input
                    type="text"
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    placeholder="Type your answer here..."
                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                  />
                )}
              </div>
            ))
            )}
          </div>

          {exam.questions && exam.questions.length > 0 && (
          <div className="mt-8 pt-8 border-t border-gray-200">
            <button
              onClick={handleSubmit}
              disabled={submitting || Object.keys(answers).length !== (exam.questions || []).length}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              {submitting ? 'Submitting...' : 'Submit Exam'}
            </button>
            <p className="text-center text-sm text-gray-500 mt-4">
              {Object.keys(answers).length} of {(exam.questions || []).length} questions answered
            </p>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamPage;
